import { useMemo } from 'react';
import {
  Alert,
  Box,
  Chip,
  Divider,
  MenuItem,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import GroupsIcon from '@mui/icons-material/Groups';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { CustomAutoComplete, CustomTextField } from '../../../../common/components';
import { OWNERSHIP_OPTIONS } from '../constants/pushNotificationConstants';

const AudienceSelector = ({
  groupOptions,
  contractOptions,
  loadingGroups,
  loadingContracts,
  filters,
  onFiltersChange,
}) => {
  const {
    audienceScope = 'all',
    groups = [],
    contracts = [],
    ownership = 'all',
  } = filters || {};

  const selectedGroups = useMemo(
    () => (groupOptions || []).filter((group) => groups.includes(group.id)),
    [groups, groupOptions]
  );

  const selectedContracts = useMemo(
    () => (contractOptions || []).filter((contract) => contracts.includes(contract.id)),
    [contracts, contractOptions]
  );

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Stack spacing={2}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
            Destinatarios
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Elegí a quiénes va dirigido el push. Podés enviarlo a todos o segmentar por contrato,
            grupo o si es poseedor.
          </Typography>
        </Box>

        <ToggleButtonGroup
          exclusive
          value={audienceScope}
          onChange={(event, value) => value && onFiltersChange('audienceScope', value)}
          size="small"
        >
          <ToggleButton value="all" sx={{ textTransform: 'none' }}>
            <PeopleAltIcon sx={{ mr: 1 }} />
            Todos los suscriptores
          </ToggleButton>
          <ToggleButton value="filtered" sx={{ textTransform: 'none' }}>
            <FilterAltIcon sx={{ mr: 1 }} />
            Filtrar envío
          </ToggleButton>
        </ToggleButtonGroup>

        {filters.audienceScope === 'filtered' && (
          <Stack spacing={2}>
            <CustomAutoComplete
              multiple
              options={groupOptions || []}
              value={selectedGroups}
              optionValueKey="id"
              inputLabel="Grupos de suscriptores"
              inputPlaceholder="Seleccioná grupos"
              disableClearable
              popupIcon={null}
              onChange={(_, values) =>
                onFiltersChange(
                  'groups',
                  Array.isArray(values) ? values.map((value) => value.id) : []
                )
              }
              getOptionLabel={(option) => option.label}
              loading={loadingGroups}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <GroupsIcon fontSize="small" color="primary" />
                    <Typography variant="body2">{option.label}</Typography>
                  </Stack>
                </li>
              )}
            />

            <CustomAutoComplete
              multiple
              options={contractOptions || []}
              value={selectedContracts}
              optionValueKey="id"
              inputLabel="Contratos"
              inputPlaceholder="Buscar por número"
              disableClearable
              popupIcon={null}
              onChange={(_, values) =>
                onFiltersChange(
                  'contracts',
                  Array.isArray(values) ? values.map((value) => value.id) : []
                )
              }
              getOptionLabel={(option) => option.label}
              loading={loadingContracts}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <AssignmentIcon fontSize="small" color="primary" />
                    <Box>
                      <Typography variant="body2">{option.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.subtitle}
                      </Typography>
                    </Box>
                  </Stack>
                </li>
              )}
              noOptionsText={loadingContracts ? 'Cargando contratos...' : 'Sin resultados'}
            />

            <CustomTextField
              label="Estado de posesión"
              select
              value={ownership}
              onChange={(event) => onFiltersChange('ownership', event.target.value)}
            >
              {OWNERSHIP_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </CustomTextField>
          </Stack>
        )}

        <Divider />

        <Alert severity="info" icon={<FilterAltIcon fontSize="small" />}>
          <Stack spacing={0.5}>
            <Typography variant="body2" fontWeight={600}>
              Resumen de alcance
            </Typography>
            {audienceScope === 'all' ? (
              <Typography variant="body2">Envío a todos los suscriptores activos.</Typography>
            ) : (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} mt={0.5}>
                <Chip
                  label={`${groups.length} grupo(s)`}
                  color={groups.length ? 'primary' : 'default'}
                  variant={groups.length ? 'filled' : 'outlined'}
                />
                <Chip
                  label={`${contracts.length} contrato(s)`}
                  color={contracts.length ? 'primary' : 'default'}
                  variant={contracts.length ? 'filled' : 'outlined'}
                />
                <Chip
                  label={
                    OWNERSHIP_OPTIONS.find((option) => option.value === ownership)?.label ||
                    'Todos'
                  }
                  color="secondary"
                  variant="outlined"
                />
              </Stack>
            )}
          </Stack>
        </Alert>
      </Stack>
    </Paper>
  );
};

export default AudienceSelector;
